import { ACTION_TYPE } from "../core/redux/actionType";
import {
  hasTrippedBreaker,
  updateBreaker,
} from "../core/redux/api/circuitBreaker/module";
import { apiBulkAddLiftAccessRules } from "../core/redux/api/endpoints";
import {
  addError,
  addLoading,
  removeLoadingAndError,
} from "../core/redux/api/module";
import {
  ApiReducer,
  makeApiAction,
  makeApiReducer,
  RsaaTypes,
} from "../core/redux/api/reduxStandardApiAction";
import { IAppState } from "../core/redux/IAppState";
import { AccessRuleMultiAddRequest } from "./accessRules/requests/accessRuleRequestBuilder";
import { LiftAccessRuleBatchSaveResult } from "./models";

export const KEY_ACTION = ACTION_TYPE.BULK_ADD_LIFT_ACCESS_RULES_REQUEST;

export const ACTIONS = [
  ACTION_TYPE.BULK_ADD_LIFT_ACCESS_RULES_REQUEST,
  ACTION_TYPE.BULK_ADD_LIFT_ACCESS_RULES_RESPONSE,
  ACTION_TYPE.BULK_ADD_LIFT_ACCESS_RULES_ERROR,
];

export const bulkAddLiftAccessRulesAction = (
  addRequest: AccessRuleMultiAddRequest,
) => {
  return makeApiAction({
    endpoint: apiBulkAddLiftAccessRules(),
    method: "POST",
    body: JSON.stringify(addRequest),

    headers: (state: IAppState) => ({
      "content-type": "application/json",
      Authorization: `Bearer ${state.auth.bearerToken}`,
    }),

    types: ACTIONS.map((type) => ({
      type,
      meta: { addRequest },
    })) as RsaaTypes,

    bailout: (state: IAppState) => hasTrippedBreaker(state, KEY_ACTION),
  });
};

const apiReducer: ApiReducer<LiftAccessRuleBatchSaveResult> = (
  state,
  action,
  error,
  isLoading,
  payload,
) => {
  if (isLoading) {
    return addLoading(state, KEY_ACTION, KEY_ACTION);
  }

  const withBreaker = updateBreaker(state, KEY_ACTION, !!error);

  if (error) {
    return addError(withBreaker, KEY_ACTION, KEY_ACTION, error);
  }

  return removeLoadingAndError(withBreaker, KEY_ACTION, KEY_ACTION);
};

const baseReducer = makeApiReducer<LiftAccessRuleBatchSaveResult>(
  ACTIONS,
  KEY_ACTION,
  apiReducer,
);

export const bulkAddLiftAccessRulesReducer = (
  state: IAppState,
  action: any,
): IAppState => {
  switch (action.type) {
    /* Progress first */
    case ACTION_TYPE.BULK_ADD_LIFT_ACCESS_RULES_BATCH_PROGRESS:
      return {
        ...state,
        bulkOperationProgress: {
          current: action.payload.current,
          total: action.payload.total,
          successCount: action.payload.successCount ?? 0,
          failureCount: action.payload.failureCount ?? 0,
          isComplete: false,
          isCancelled: false,
        },
      };

    /* Complete */
    case ACTION_TYPE.BULK_ADD_LIFT_ACCESS_RULES_BATCH_COMPLETE:
      return {
        ...state,
        bulkOperationProgress: {
          ...state.bulkOperationProgress!,

          // Final counts from payload
          successCount:
            action.payload?.successCount ??
            state.bulkOperationProgress?.successCount ??
            0,

          failureCount:
            action.payload?.failureCount ??
            state.bulkOperationProgress?.failureCount ??
            0,

          isComplete: true,
          isCancelled: action.payload?.cancelled ?? false,
        },
      };

    /* Reset */
    case ACTION_TYPE.BULK_ADD_LIFT_ACCESS_RULES_BATCH_RESET:
      return {
        ...state,
        bulkOperationProgress: undefined,
      };

    default:
      return baseReducer(state, action);
  }
};
